import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PortalService } from '../../../../../../services/portal.service';
import SignaturePad from 'signature_pad';
import { EnrollmentFormService } from '../../../../../../services/EnrollmentFormService';
import { ActivatedRoute, Router } from '@angular/router';
import { SuccessComponent } from '../../../modals/enrollment-success/success.component';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../../../../environments/environment';
import { TelemetryService } from '../../../../../../services/telemetry.service';

@Component({
  selector: 'app-signatures',
  templateUrl: './signatures.component.html',
  styleUrls: ['./signatures.component.scss']
})
export class SignaturesComponent implements OnInit, AfterViewInit {
  signatureForm!: FormGroup;
  efsForm: FormGroup<any>;
  enrollmentId: any;
  manufacturerId = environment.manufacturerID;
  declarationText: string;
  authorizationText: string;
  signaturePad1!: SignaturePad;
  signaturePad2!: SignaturePad;
  _isSignature1Accepted = false;
  _isSignature2Accepted = false;
  isSubmitDisabled = true; // New variable to control button state
  isLoading = false;
  selectedSignatures: {
    file: File;
    preview: string;
    description: string;
    name: string;
    type: string;
    attachmentId?: number;
  }[] = [];

  @ViewChild('signaturePad1', { static: false }) signaturePadElement1!: ElementRef<HTMLCanvasElement>;
  @ViewChild('signaturePad2', { static: false }) signaturePadElement2!: ElementRef<HTMLCanvasElement>;
  currentSequenceStep: string;

  get isSignature1Accepted(): boolean{
    return this._isSignature1Accepted;
  }
  get isSignature2Accepted(): boolean{
    return this._isSignature2Accepted;
  }

  set isSignature1Accepted(value: boolean){
    this._isSignature1Accepted = value;
    if(value)
      this.updateSelectedSignatures();
  }
  set isSignature2Accepted(value: boolean){
    this._isSignature2Accepted = value;
    if(value)
      this.updateSelectedSignatures();
  }

  updateSelectedSignatures(){
    this.selectedSignatures = [];
    this.portalService.getEnrollmentAttachment(this.enrollmentId).subscribe({
      next: (data: any) => {
        const signature1 = data.find((attachment: any) => attachment.enrollmentAttachmentTypeId === 10);
        const signature2 = data.find((attachment: any) => attachment.enrollmentAttachmentTypeId === 11);
        if (signature1) {
          let name = signature1.filePath.split('/').pop();
          let type = signature1.filePath.split('.').pop();
          this.selectedSignatures.push({
            file: new File([], name),
            preview: 'data:image/png;base64,' + signature1.fileDocumentBase64Content,
            name,
            type: type === "pdf" ? "pdf" : "image",
            description: signature1.description,
            attachmentId: signature1.attachmentId,
          });
        }
        
        if (signature2) {
          let name = signature2.filePath.split('/').pop();
          let type = signature2.filePath.split('.').pop();
          this.selectedSignatures.push({
            file: new File([], name),
            preview: 'data:image/png;base64,' + signature2.fileDocumentBase64Content,
            name,
            type: type === "pdf" ? "pdf" : "image",
            description: signature2.description,
            attachmentId: signature2.attachmentId,
          });
        }
      },
      error: (error: any) => {
        console.error('Error fetching attachments:', error);
      }
    }); 
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private efs: EnrollmentFormService,
    private router: Router,
    private fb: FormBuilder,
    private portalService: PortalService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private telemetryService: TelemetryService) {
    this.efsForm = this.efs.enrollmentForm as FormGroup;
  }

  ngOnInit(): void {
    this.currentSequenceStep = localStorage.getItem('CurrentEnrollmentStep');
    this.efs.enrollmentID$.subscribe((id) => {
      this.enrollmentId = id;
    });
    this.route.paramMap.subscribe(params => {
      if (params.get('id')) this.enrollmentId = params.get('id');
    });
    this.setSpecificContent();
    this.signatureForm = this.fb.group({
      ApplicatantDeclarationAuthorization_Signature: ['', Validators.required],
      HealthInfoSharing_Signature: ['', Validators.required]
    });

    // Listen to form changes to update the button state
    this.signatureForm.statusChanges.subscribe(status => {
      this.isSubmitDisabled = status !== 'VALID';
    });
  }

  ngAfterViewInit(): void {
    this.initializeSignaturePad();
  }

  private initializeSignaturePad() {
    this.signaturePad1 = new SignaturePad(this.signaturePadElement1.nativeElement, {
      minWidth: 0.5,
      maxWidth: 1,
      penColor: 'rgb(0, 0, 0)',
    });
    this.signaturePad2 = new SignaturePad(this.signaturePadElement2.nativeElement, {
      minWidth: 0.5,
      maxWidth: 1,
      penColor: 'rgb(0, 0, 0)',
    });


    if (this.efs.currentEnrollmentObject?.enrollmentAttachmentViewDTOs?.length) {
      const sign1 = this.efs.currentEnrollmentObject?.enrollmentAttachmentViewDTOs.find((attachment: any) => attachment.enrollmentAttachmentTypeId === 10);
      if (sign1) {
        const base64Sign = 'data:image/png;base64,' + sign1.fileDocumentBase64Content;
        this.signatureForm.patchValue({ ApplicatantDeclarationAuthorization_Signature: base64Sign });
        this.signaturePad1.fromDataURL(base64Sign);
        this.isSignature1Accepted = true;
        this.signaturePad1.off();
      }
      const sign2 = this.efs.currentEnrollmentObject?.enrollmentAttachmentViewDTOs.find((attachment: any) => attachment.enrollmentAttachmentTypeId === 11);
      if (sign2) {
        const base64Sign = 'data:image/png;base64,' + sign2.fileDocumentBase64Content;
        this.signatureForm.patchValue({ HealthInfoSharing_Signature: base64Sign });
        this.signaturePad2.fromDataURL(base64Sign);
        this.isSignature2Accepted = true;
        this.signaturePad2.off();
      }
    }
  }

  setSpecificContent(): void {
    this.portalService.getDataByConsentType('Signature Text 1').subscribe(data => {
      if (data.length) this.declarationText = data[0].contentText;
    });

    this.portalService.getDataByConsentType('Signature Text 2').subscribe(data => {
      if (data.length) this.authorizationText = data[0].contentText;
    });
  }

  private createSignatureFormData(
    signatureDataURL: string,
    attachmentTypeId: string,
    description: string
  ): FormData {
    const formData = new FormData();
    formData.append('EnrollmentId', this.enrollmentId.toString());
    formData.append('ManufacturerId', this.manufacturerId.toString());

    const signatureBlob = this.base64ToBlob(signatureDataURL);
    formData.append('EnrollmentAttachmentDocumentDTOs[0].EnrollmentAttchmentTypeId', attachmentTypeId);
    formData.append('EnrollmentAttachmentDocumentDTOs[0].Description', description);
    formData.append('EnrollmentAttachmentDocumentDTOs[0].FileDocument', signatureBlob, `${description}.png`);

    return formData;
  }

  acceptSignature1() {
    if (this.signaturePad1.isEmpty()) {
      return;
    }
    this.telemetryService.logEvent('Accept Signature Clicked', {
      step: 'Signature - Applicant Declaration and Authorization',
      timestamp: new Date().toISOString(),
    });

    const base64Signature = this.signaturePad1.toDataURL();
    this.signatureForm.patchValue({ ApplicatantDeclarationAuthorization_Signature: base64Signature });

    const formData = this.createSignatureFormData(
      base64Signature,
      '10',
      'ApplicatantDeclarationAuthorization_Signature'
    );

    this.portalService.uploadDocumentAttachment(formData).subscribe({
      next: (response) => {
        this.isSignature1Accepted = true;
        this.signaturePad1.off();
      },
      error: (error) => {
        console.error('Error uploading Applicatant Declaration Authorization Signature:', error);
      }
    });
  }

  acceptSignature2() {
    if (this.signaturePad2.isEmpty()) {
      return;
    }
    this.telemetryService.logEvent('Accept Signature Clicked', {
      step: 'Signature - Use and Disclosure of Personal Health Information',
      timestamp: new Date().toISOString(),
    });

    const base64Signature = this.signaturePad2.toDataURL();
    this.signatureForm.patchValue({ HealthInfoSharing_Signature: base64Signature });

    const formData = this.createSignatureFormData(
      base64Signature,
      '11',
      'HealthInfoSharing_Signature'
    );

    this.portalService.uploadDocumentAttachment(formData).subscribe({
      next: (response) => {
        this.isSignature2Accepted = true;
        this.signaturePad2.off();
      },
      error: (error) => {
        console.error('Error uploading Health Info Sharing Signature:', error);
      }
    });
  }

  clearSignature1() {
    this.telemetryService.logEvent('Clear Signature Clicked', {
      step: 'Signature - Applicant Declaration and Authorization',
      timestamp: new Date().toISOString(),
    });
    this.removeSignature('sign1');
  }

  clearSignature2() {
    this.telemetryService.logEvent('Clear Signature Clicked', {
      step: 'Signature - Use and Disclosure of Personal Health Information',
      timestamp: new Date().toISOString(),
    });
    this.removeSignature('sign2');
  }

  removeSignature(field:string): void {
    let fileToRemove: any;
    if (this.isSignature1Accepted && field == 'sign1'){
      fileToRemove = this.selectedSignatures.find((attachment: any) => attachment.description === 'ApplicatantDeclarationAuthorization_Signature');
      this.selectedSignatures = this.selectedSignatures.filter((attachment: any) =>  !(attachment.enrollmentAttachmentTypeId === 'ApplicatantDeclarationAuthorization_Signature'));
    }
    else if (this._isSignature2Accepted && field == 'sign2'){
      fileToRemove = this.selectedSignatures.find((attachment: any) => attachment.description === 'HealthInfoSharing_Signature');
      this.selectedSignatures = this.selectedSignatures.filter((attachment: any) =>  !(attachment.enrollmentAttachmentTypeId === 'HealthInfoSharing_Signature'));
    }
      
    if(fileToRemove){
      if (!fileToRemove.attachmentId) {
        console.error('Attachment ID not found for the file:', fileToRemove);
        return;
      }

      this.portalService.removeDocumentAttachment(fileToRemove.attachmentId).subscribe({
        next: (response) => {
          if (this.isSignature1Accepted && field == 'sign1'){
            this.signaturePad1.clear();
            this.signaturePad1.on();
            this.isSignature1Accepted = false;
            this.signatureForm.patchValue({ ApplicatantDeclarationAuthorization_Signature: '' });
          }
          else if (this._isSignature2Accepted && field == 'sign2'){
            this.signaturePad2.clear();
            this.signaturePad2.on();
            this.isSignature2Accepted = false;
            this.signatureForm.patchValue({ HealthInfoSharing_Signature: '' });
          }
          // Trigger change detection
          this.cdr.detectChanges();

        },
        error: (error: any) => {
          console.error('Error removing document:', error);
        }
      });
    }
    else{
      if (field == 'sign1'){
        this.signaturePad1.clear();
        this.signaturePad1.on();
        this.isSignature1Accepted = false;
        this.signatureForm.patchValue({ ApplicatantDeclarationAuthorization_Signature: '' });
      }
      else if (field == 'sign2'){
        this.signaturePad2.clear();
        this.signaturePad2.on();
        this.isSignature2Accepted = false;
        this.signatureForm.patchValue({ HealthInfoSharing_Signature: '' });
      }
      // Trigger change detection
      this.cdr.detectChanges();
    }
  }

  base64ToBlob(base64: string): Blob {
    const byteString = atob(base64.split(',')[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([intArray], { type: 'image/png' });
  }

  openModal(): void {
    const dialogRef = this.dialog.open(SuccessComponent, {
      minWidth: '50vw', // Set modal width
      minHeight: '70vh', // Set modal height
      autoFocus: false, // Disable autofocus
      disableClose: true, // Prevent closing by clicking outside
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        console.log('User selected:', result); // Handle the success response here
      } else {
        console.log('Modal closed without selection'); // Handle case where modal is closed without action
      }
    });
  }

  onSubmit() {
    this.telemetryService.logEvent('Submit Application Clicked', {
      timestamp: new Date().toISOString(),
    });

    this.isLoading = true;
    this.signatureForm.patchValue({
      lastEnrollmentStep: this.currentSequenceStep
    });
    if (this.signatureForm.valid) {
      this.portalService.submitEnrollment(this.efsForm.value, this.enrollmentId).subscribe((value: any) => {
        this.isLoading = false;
        this.efs.setEnrollmentID(value.enrollmentId);
        this.openModal();
        this.signatureForm.get('ApplicatantDeclarationAuthorization_Signature')?.markAsUntouched();
        this.signatureForm.get('HealthInfoSharing_Signature')?.markAsUntouched();
      });
      this.efs.resetForm();
    }
  }
}
